import * as T from 'three';
// A jet of water as one stream. Points leave the nozzle at a fixed rate and
// fly like thrown stones; while they keep coming, the stream is drawn as a
// tube through them in the order they left, with round ends. The same flow
// passes every section, so the stream thins where it has sped up
// (radius ~ sqrt(v0 / v)). A pause in the flow starts a new tube.
export class JetStream{
  constructor({capacity=200,gravity=9.8,radius=.02,interval=1/120,segments=8}={}){
    Object.assign(this,{capacity,gravity,radius,interval,segments});
    this.points=[];this.pending=0;this.flowing=false;this.run=0;
    const rings=capacity+64;this.maxRings=rings;
    this.positions=new Float32Array(rings*segments*3);this.normals=new Float32Array(rings*segments*3);this.indices=new Uint32Array(rings*segments*6);
    const g=new T.BufferGeometry();
    g.setAttribute('position',new T.BufferAttribute(this.positions,3).setUsage(T.DynamicDrawUsage));
    g.setAttribute('normal',new T.BufferAttribute(this.normals,3).setUsage(T.DynamicDrawUsage));
    g.setIndex(new T.BufferAttribute(this.indices,1).setUsage(T.DynamicDrawUsage));
    // Empty bounds keep the stream out of the play fit.
    g.setDrawRange(0,0);g.boundingBox=new T.Box3();g.boundingSphere=new T.Sphere(new T.Vector3(),1e3);
    this.geometry=g;
  }
  get count(){return this.points.length;}
  radiusOf(p){return this.radius*Math.sqrt(p.speed/Math.max(1e-6,Math.hypot(p.vx,p.vy,p.vz)));}
  // Water leaves the nozzle at this velocity during the last dt seconds.
  // Call after step(), so each new point has flown for its own share of dt.
  emit(origin,velocity,dt){
    if(!this.flowing){this.flowing=true;this.run++;this.pending=Math.max(this.pending,this.interval);}
    else this.pending+=dt;
    const speed=Math.hypot(velocity[0],velocity[1],velocity[2]),g=this.gravity;let made=0;
    while(this.pending>=this.interval&&this.points.length<this.capacity){
      this.pending-=this.interval;const age=Math.min(dt,this.pending);
      this.points.push({x:origin[0]+velocity[0]*age,y:origin[1]+velocity[1]*age-g*age*age/2,z:origin[2]+velocity[2]*age,
        vx:velocity[0],vy:velocity[1]-g*age,vz:velocity[2],speed,run:this.run,age});
      made++;
    }
    return made;
  }
  stop(){this.flowing=false;this.pending=0;}
  // Points fly under gravity; land(point) returns true to remove one.
  step(dt,land){
    const g=this.gravity;let w=0;
    for(const p of this.points){p.x+=p.vx*dt;p.y+=p.vy*dt-g*dt*dt/2;p.vy-=g*dt;p.age+=dt;if(!land(p))this.points[w++]=p;}
    this.points.length=w;
  }
  clear(){this.points.length=0;this.stop();this.geometry.setDrawRange(0,0);}
  // Rebuild the tubes; returns how many streams were drawn.
  build(){
    const P=this.points,S=this.segments,pos=this.positions,nor=this.normals,idx=this.indices;
    const t=new T.Vector3(),n=new T.Vector3(),b=new T.Vector3(),c=new T.Vector3(),up=new T.Vector3(0,1,0),side=new T.Vector3(1,0,0);
    let ring=0,k=0,runs=0;
    const addRing=(cx,cy,cz,r,along)=>{
      // A ring around the axis; `along` tips the normals for the round ends.
      const o=ring*S*3,ca=Math.cos(along),sa=Math.sin(along);
      for(let j=0;j<S;j++){
        const a=j/S*Math.PI*2,rx=n.x*Math.cos(a)+b.x*Math.sin(a),ry=n.y*Math.cos(a)+b.y*Math.sin(a),rz=n.z*Math.cos(a)+b.z*Math.sin(a),v=o+j*3;
        pos[v]=cx+rx*r;pos[v+1]=cy+ry*r;pos[v+2]=cz+rz*r;
        nor[v]=rx*ca+t.x*sa;nor[v+1]=ry*ca+t.y*sa;nor[v+2]=rz*ca+t.z*sa;
      }
      ring++;
    };
    const join=(first,last)=>{for(let a=first;a<last;a++)for(let j=0;j<S;j++){const p=a*S+j,q=a*S+(j+1)%S;idx[k++]=p;idx[k++]=q;idx[k++]=p+S;idx[k++]=q;idx[k++]=q+S;idx[k++]=p+S;}};
    const frame=(i,s,e)=>{
      // Tangent along the array (front to nozzle), then a minimally turning frame.
      const a=P[Math.max(s,i-1)],z=P[Math.min(e,i+1)];
      if(a===z)t.set(-a.vx,-a.vy,-a.vz);else t.set(z.x-a.x,z.y-a.y,z.z-a.z);
      if(t.lengthSq()<1e-12)t.set(0,-1,0);t.normalize();
      if(i===s){n.crossVectors(t,Math.abs(t.y)<.9?up:side).normalize();}
      else{n.addScaledVector(t,-n.dot(t));if(n.lengthSq()<1e-12)n.crossVectors(t,Math.abs(t.y)<.9?up:side);n.normalize();}
      b.crossVectors(t,n);
    };
    const caps=[Math.PI/2,Math.PI/3,Math.PI/6];
    for(let s=0;s<P.length;){
      let e=s;while(e+1<P.length&&P[e+1].run===P[s].run)e++;
      if(ring+(e-s+1)+6>this.maxRings)break;
      const first=ring;runs++;
      frame(s,s,e);const r0=this.radiusOf(P[s]);
      for(const f of caps){c.set(P[s].x,P[s].y,P[s].z).addScaledVector(t,-r0*Math.sin(f));addRing(c.x,c.y,c.z,r0*Math.cos(f),-f);}
      for(let i=s;i<=e;i++){frame(i,s,e);addRing(P[i].x,P[i].y,P[i].z,this.radiusOf(P[i]),0);}
      const r1=this.radiusOf(P[e]);
      for(const f of [...caps].reverse()){c.set(P[e].x,P[e].y,P[e].z).addScaledVector(t,r1*Math.sin(f));addRing(c.x,c.y,c.z,r1*Math.cos(f),f);}
      join(first,ring-1);s=e+1;
    }
    const g=this.geometry;g.setDrawRange(0,k);
    g.attributes.position.needsUpdate=true;g.attributes.normal.needsUpdate=true;g.index.needsUpdate=true;
    return runs;
  }
}
